import React, { useMemo } from 'react';
import { Button, Link } from 'react-aria-components';

import { tv } from '../../tv';
import { SlotPadding, SlotStyleContext } from '../slots/slot-context';

/**
 * ComplyKit Card: the styled container.
 *
 * The one container in the vocabulary. Product code chooses a card and a
 * variant, never a border width, which is what lets a restyle be a token
 * change rather than a sweep across call sites.
 *
 * Composes from the SHARED slots (Header, Content, Footer, Bleed), not from
 * parts of its own: a header means the same thing in a card, a dialog and a
 * drawer, so it is defined once. See
 * docs/specs/frontend/design-system/naming-and-composition.md.
 *
 * Not every container is a Card. A wrapper with no visual identity is a plain
 * element, spacing belongs to a layout component, and text styling belongs to
 * Text. Reaching for a Card because it is available is the mistake the
 * vocabulary exists to prevent (see vision.md).
 */
export interface CardProps {
  /** The card's content, usually the shared Header, Content and Footer slots */
  children?: React.ReactNode;
  /**
   * Visual emphasis. `outlined` is the default because a bordered surface is
   * how nearly every container in the product reads today; `elevated` is for
   * surfaces that float above the page, and `flat` for a filled region that
   * needs no separation from its background.
   */
  variant?: 'outlined' | 'elevated' | 'flat';
  /**
   * Inset applied to the card's slots. Set here rather than on each slot, so
   * one card cannot have a compact header and a spacious body.
   */
  padding?: SlotPadding;
  /**
   * Fills the height of whatever lays the card out, so a row of cards shares
   * one height instead of each sizing to its own content. The card also
   * becomes a column, which lets a content slot take the leftover space.
   */
  isFilled?: boolean;
  /**
   * The element to render. Use `article` or `section` where it carries
   * meaning. Deliberately excludes `a` and `button`: a card becomes
   * actionable by being given somewhere to go (`href`) or something to do
   * (`onPress`), which switches the element, the keyboard behaviour and the
   * focus ring together. Choosing the tag by hand gets one of the three
   * right and silently drops the other two.
   */
  as?: 'div' | 'article' | 'section' | 'li';
  /**
   * Where the card goes. Renders the card as a real link, so it can be
   * opened in a new tab, copied and reached by keyboard, and it shows the
   * interactive hover, press and focus states.
   */
  href?: string;
  /** Link target, for the rare card that opens away from the app. */
  target?: '_blank';
  /**
   * What the card does. Renders the card as a real button for an action on
   * the page rather than a navigation, with the same interactive states.
   */
  onPress?: () => void;
  /**
   * The one tone a card wears: `ai`, the signature ring around content the
   * agent produced or is producing (ai.md). No other tone, by ruling: a
   * ring that only moves a colour would be the card's only signal, where
   * the AI frame always sits beside the sparkle or the chip.
   */
  tone?: 'ai';
  /** The id of the card element */
  id?: string;
  /** Accessible label, when the card is a labelled region */
  'aria-label'?: string;
  /** The id of an element labelling the card */
  'aria-labelledby'?: string;
}

/* A card is a passive container, so its edge uses border-subtle (the
 * non-interactive step) rather than the control step, which reads as a hard
 * line a card should never have.
 *
 * A card is `raised`: one step away from the page in either mode. Elevation
 * in dark is carried by that lift rather than by shadow, which barely
 * registers on a dark ground, so `elevated` adds a shadow in light and goes a
 * further step in both. `outlined` carries the raised elevation, a hairline
 * under the edge (a lit top edge in dark), so it sits on the page. */
export const cardStyles = tv({
  /* The card does not clip. Clipping is what a full-bleed child needs so its
   * corners follow the card's, and that belongs to the child: putting it here
   * traps everything else too, including content deliberately placed to break
   * the edge, such as a logo that overlaps the boundary. */
  base: ['ck:rounded-3 ck:surface-raised'],
  variants: {
    variant: {
      outlined: 'ck:border ck:border-border-subtle ck:shadow-raised',
      elevated: 'ck:border ck:border-border-subtle ck:surface-overlay ck:shadow-sm',
      flat: 'ck:surface-recessed',
    },
    /* A filled card fills its row, so its content takes the leftover space
     * rather than shrink-wrapping at the top: a widget that centres itself
     * has a full-height box to centre against however short its content is. */
    /* The AI tone rings the card with its signature (tokens.md); the card's
     * own edge goes transparent underneath the ring. */
    toned: {
      true: 'ck:ring-signature ck:border-transparent',
      false: '',
    },
    filled: {
      true: 'ck:h-full ck:flex ck:flex-col ck:[&>[data-slot=content]]:flex-1',
      false: '',
    },
    /* An actionable card says so when it is pointed at: the edge moves to
     * the interactive border, which reads from the ACCENT scale so the
     * feedback follows the palette, and the surface lifts. Pressing settles
     * the shadow back down rather than lifting further, so the card reads as
     * being pushed.
     *
     * The states come from React Aria's data attributes: the Tailwind plugin
     * folds data-hovered into `hover:` and data-focus-visible into
     * `focus-visible:`, so on a RAC element these follow RAC's own state
     * tracking rather than raw CSS, which is what stops a tap from leaving a
     * hover appearance stuck on a touch screen. `pressed:` targets
     * data-pressed, and the hover+pressed pair exists because the hover rule
     * otherwise outweighs the press one while the pointer is still down.
     *
     * `text-left` undoes the user agent's centring of button text; a card
     * that becomes a button must not change how its content reads. */
    interactive: {
      true: [
        /* The group marker is contract: descendants may react to the card
         * being pointed at (HoverSwap reads it), and the group-hover variant
         * carries the same touch-safe RAC selector as hover itself. */
        'ck:group',
        'ck:cursor-pointer ck:text-left',
        'ck:transition',
        'ck:hover:border-border-interactive ck:hover:shadow-md',
        'ck:pressed:shadow-sm ck:hover:pressed:shadow-sm',
        'ck:focus-visible:outline-2 ck:focus-visible:outline-offset-2 ck:focus-visible:outline-border-interactive',
      ],
      false: '',
    },
  },
  // Pointed at, a ringed card keeps the ring as its only edge.
  compoundVariants: [{ toned: true, interactive: true, class: 'ck:hover:border-transparent' }],
  defaultVariants: { variant: 'outlined', filled: false, interactive: false, toned: false },
});

export const Card = React.forwardRef<HTMLElement, CardProps>(function Card(
  {
    children,
    variant = 'outlined',
    padding = 'default',
    isFilled = false,
    as: As = 'div',
    href,
    target,
    onPress,
    tone,
    ...props
  },
  ref,
) {
  // The slots read their inset from here, so the card sets it once.
  const slotStyle = useMemo(() => ({ padding }), [padding]);

  /* Somewhere to go or something to do is what makes the card actionable,
   * and which of the two decides the element. Inferring it means a card
   * cannot be given a click without also getting the right element, the
   * keyboard behaviour and the focus ring; there is no flag to forget. */
  const className = cardStyles({
    variant,
    filled: isFilled,
    interactive: Boolean(href || onPress),
    toned: Boolean(tone),
  });

  if (href) {
    return (
      <SlotStyleContext.Provider value={slotStyle}>
        <Link
          {...props}
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          className={className}
          data-component="card"
          data-tone={tone}
        >
          {children}
        </Link>
      </SlotStyleContext.Provider>
    );
  }

  if (onPress) {
    return (
      <SlotStyleContext.Provider value={slotStyle}>
        <Button
          {...props}
          ref={ref as React.Ref<HTMLButtonElement>}
          onPress={onPress}
          className={className}
          data-component="card"
          data-tone={tone}
        >
          {children}
        </Button>
      </SlotStyleContext.Provider>
    );
  }

  return (
    <SlotStyleContext.Provider value={slotStyle}>
      <As
        {...props}
        // A polymorphic element has no single ref type across div/article/li,
        // so the cast is confined here rather than pushed onto callers.
        ref={ref as React.Ref<never>}
        className={className}
        data-component="card"
        data-tone={tone}
      >
        {children}
      </As>
    </SlotStyleContext.Provider>
  );
});
